import {
  AdvancedSearchActions,
  AdvancedSearchActionTypes, FetchAgencyInvolvementOptionsSuccess,
  InitAdvancedSearchForm
} from '@app/pcs/actions/advanced-search';
import { DictionaryEntity, DossierSearchType, Filter, Label } from '@app/pcs/models';
import {
  addExclusiveFields,
  AdvancedSearchAdditionalFields,
  getAdvancedSearchAdditionalFields,
  omitFieldsBySearchType,
  omitFieldsUsedInGroup,
  omitMutuallyExclusiveFields
} from '@app/pcs/reducer/advanced-search/additional-fields';
import { AGENCY_INVOLVEMENT } from '@app/pcs/reducer/advanced-search/search-form';
import { createHandler, createReducer, hasNoValue } from '@util';
import * as R from 'ramda';

export interface State {
  additionalFields: AdvancedSearchAdditionalFields;
  labelsForUser: Label[];
  transitions: any[];
  processTypes: DictionaryEntity[];
  dossierStatuses: DictionaryEntity[];
  filter: Filter;
  searchType: DossierSearchType;
  hasOperationInProgress: boolean;
  agencyInvolvementOptions: DictionaryEntity[];
  searchedPermanentAcceptance: boolean;
}

export const initialState: State = {
  additionalFields: null,
  labelsForUser: [],
  transitions: [],
  processTypes: null,
  dossierStatuses: null,
  filter: null,
  searchType: null,
  hasOperationInProgress: false,
  agencyInvolvementOptions: [],
  searchedPermanentAcceptance: false
};

const initAdditionalFields = (state: State, {payload: {searchCriteria, searchType}}: InitAdvancedSearchForm) =>
  R.mergeRight(state, {
    additionalFields: R.compose((additionalFields) => omitFieldsBySearchType(searchType, additionalFields),
      omitMutuallyExclusiveFields, omitFieldsUsedInGroup, getAdvancedSearchAdditionalFields)(searchCriteria, searchType),
    searchType
  });

const toggleAdditionalField = (state: State, {payload: key}) =>
  R.evolve({
    additionalFields: R.compose(omitMutuallyExclusiveFields, omitFieldsUsedInGroup, addExclusiveFields, R.set(R.lensProp(key), !state.additionalFields[key]))
  }, state);

const toggleAllAdditionalFields = (state: State, {payload: value}) =>
  R.evolve({
    additionalFields: R.compose(omitMutuallyExclusiveFields, omitFieldsUsedInGroup,  addExclusiveFields, R.map(R.always(value)))
  }, state);

const updateLabelsForUser = (state: State, {payload: labelsForUser}) =>
  R.mergeRight(state, {
    labelsForUser
  });

const updateTransitions = (state: State, {payload: transitions}) =>
  R.mergeRight(state, {
    transitions
  });

const updateProcessTypes = (state: State, {payload: processTypes}): State =>
  R.mergeRight(state, {
    processTypes
  });

const updateDossierStatuses = (state: State, {payload: dossierStatuses}): State =>
  R.mergeRight(state, {
    dossierStatuses
  });

const setFilter = (state: State, {payload: filter}): State =>
  R.mergeRight(state, {
    filter
  });

const removeAdditionalFields = (state: State, {payload: fields}): State => R.evolve({
  additionalFields: R.omit(fields)
}, state);

const resetState = (state: State): State =>
  R.mergeRight(state, {
    dossierStatuses: null,
    processTypes: null,
    filter: null,
    hasOperationInProgress: false
  });

const fetchAgencyInvolvementOptions = (state: State, {payload: agencyInvolvementOptions}: FetchAgencyInvolvementOptionsSuccess) => {
  const currentAdditionalFields = R.prop('additionalFields', state);
  const additionalFields = hasNoValue(agencyInvolvementOptions)
    ? R.omit([AGENCY_INVOLVEMENT], currentAdditionalFields)
    : currentAdditionalFields;

  return R.mergeRight(state, {
    additionalFields,
    agencyInvolvementOptions
  });
};

const updateHasOperationInProgress = (state: State, {payload: {hasOperationInProgress}}) => R.mergeRight(state, {hasOperationInProgress});

const updateSearchedPermanentAcceptance = (state: State, {payload: searchedPermanentAcceptance}) => R.mergeRight(state, {searchedPermanentAcceptance});

export const reducer = createReducer<State, AdvancedSearchActions>([
  createHandler(AdvancedSearchActionTypes.InitAdvancedSearchForm, initAdditionalFields),
  createHandler(AdvancedSearchActionTypes.ToggleAdditionalField, toggleAdditionalField),
  createHandler(AdvancedSearchActionTypes.SetVisibilityOfAdditionalFields, toggleAllAdditionalFields),
  createHandler(AdvancedSearchActionTypes.FetchLabelsForUserSuccess, updateLabelsForUser),
  createHandler(AdvancedSearchActionTypes.UpdateTransitions, updateTransitions),
  createHandler(AdvancedSearchActionTypes.UpdateProcessTypes, updateProcessTypes),
  createHandler(AdvancedSearchActionTypes.UpdateDossierStatuses, updateDossierStatuses),
  createHandler(AdvancedSearchActionTypes.SetFilter, setFilter),
  createHandler(AdvancedSearchActionTypes.RemoveAdditionalFields, removeAdditionalFields),
  createHandler(AdvancedSearchActionTypes.ResetState, resetState),
  createHandler(AdvancedSearchActionTypes.CheckBulkEditingProgressSuccess, updateHasOperationInProgress),
  createHandler(AdvancedSearchActionTypes.UpdateSearchedPermanentAcceptance, updateSearchedPermanentAcceptance),
  createHandler(AdvancedSearchActionTypes.FetchAgencyInvolvementOptionsSuccess, fetchAgencyInvolvementOptions)
], initialState);
